import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../hooks/useCart";
import { reservarStock, reservaCheckout, getMetodosEnvio, crearOrdenPedido } from "../../../services/cliente/compras";
import "./Checkout.css";
import phoneCountries from "../../../catalogs/phoneCountries";
import { validateCart } from "../../../services/public/cartValidation";
import { firmaCarrito } from "../../../utils/cart";
import {
  getDireccionesCliente,
  getPerfilCliente,
} from "../../../services/cliente/perfilCheckoutCliente";
import {
  createDireccionCliente,
  updatePerfilCliente,
} from "../../../services/cliente/perfilcliente";

function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, subtotal, coreChargeTotal, grandTotal, ready } = useCart();

  const token = localStorage.getItem("cliente_token");
  let cliente = null;
  try {
    cliente = JSON.parse(localStorage.getItem("cliente"));
  } catch {
    cliente = null;
  }

  const isLogged = !!token && !!cliente;
  const hasTelefono = !!cliente?.telefono && !!cliente?.codigo_telefono;
  const needsTelefono = isLogged && !hasTelefono;
  const [direcciones, setDirecciones] = useState([]);

  /* Los datos de facturacion se piden al backend y no se sacan del
     localStorage: ahi quedan congelados desde el login, asi que un cliente
     que completa su NIT en el perfil seguiria facturando sin el hasta que
     vuelva a entrar. */
  const [perfil, setPerfil] = useState(null);

  /* Los datos de facturacion se pueden completar sin salir del checkout:
     mandar al cliente a su cuenta a mitad de una compra es perder la
     compra. */
  const [editandoFactura, setEditandoFactura] = useState(false);
  const [guardandoFactura, setGuardandoFactura] = useState(false);
  const [errorFactura, setErrorFactura] = useState(null);
  const [factura, setFactura] = useState({
    nombre: "",
    apellido: "",
    correo: "",
    codigo_telefono: "",
    telefono: "",
    razon_social: "",
    vat_tax_id: "",
    correo_facturacion: "",
  });
  const [loadingPerfil, setLoadingPerfil] = useState(true);
  const perfilCargadoRef = useRef(false);
  const clienteCargadoRef = useRef(false);
  const metodosCargadosRef = useRef(false);
  const carritoRevisadoRef = useRef(false);
  /* Con que llego el cliente al checkout. Si esto cambia despues es que lo
     movio desde otra pestaña. */
  const firmaAlEntrarRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metodosEnvio, setMetodosEnvio] = useState([]);
  const [loadingMetodosEnvio, setLoadingMetodosEnvio] = useState(true);

  const [form, setForm] = useState({
    nombre: "",
    apellido: "",
    correo: "",
    telefono_codigo: phoneCountries[0].code,
    telefono_numero: "",
    direccion_guardada: "",
    guardar_direccion: false,
    direccion_nombre: "",
    direccion_destinatario: "",
    direccion_empresa: "",
    direccion_linea_1: "",
    direccion_linea_2: "",
    ciudad: "",
    estado: "",
    codigo_postal: "",
    pais: "",
    direccion_principal: true,
    metodo_envio: "",
    envio_empresa: "",
    envio_cuenta: "",
    pago_modo: "",
    no_po: "",
    nombre_contacto: "",
  });

  /* Mientras el perfil viaja, lo que haya en el localStorage sirve para no
     mostrar la ficha vacia. */
  const datosCliente = perfil || cliente || {};
  const esEmpresa = datosCliente.tipo_cliente === "comercial";

  const [step, setStep] = useState(2);
  const [errors, setErrors] = useState({});

  const formatMoney = (value) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(Number(value));

  const selectedMetodo = useMemo(() => {
    return (
      metodosEnvio.find(
        (m) => String(m.metodo_envio_id) === String(form.metodo_envio)
      ) || null
    );
  }, [metodosEnvio, form.metodo_envio]);

  const permiteTarjeta = Number(selectedMetodo?.permite_tarjeta) === 1;
  const permiteAgente = Number(selectedMetodo?.permite_agente) === 1;
  const requiresAccount = Number(selectedMetodo?.requiere_cuenta) === 1;
  const isStoreSelect = !!selectedMetodo && !permiteTarjeta && permiteAgente;

  const requiredByStep = {
    2: [
      "direccion_nombre",
      // Sin quien recibe, la etiqueta de envio sale sin nombre.
      "direccion_destinatario",
      "telefono_numero",
      "direccion_linea_1",
      "ciudad",
      "estado",
      "codigo_postal",
      "pais",
    ],
    3: ["metodo_envio"],
    4: [],
  };

  /* Al entrar se revisa el carrito de nuevo.
   *
   * Antes la unica revision estaba en el boton del carrito. Entre eso y
   * apretar el boton final pueden pasar diez minutos llenando el
   * formulario, y en ese rato el producto se puede agotar, el admin lo
   * puede dar de baja o le puede cambiar el precio. El cliente se
   * enteraba en el ultimo paso, con un error, y sin forma de arreglar el
   * carrito desde ahi. Si algo cambio se lo manda de vuelta al carrito,
   * que es la pantalla donde puede hacer algo al respecto.
   */
  useEffect(() => {
    if (!ready) return;
    if (carritoRevisadoRef.current) return;
    if (!cart || cart.length === 0) return;

    carritoRevisadoRef.current = true;
    firmaAlEntrarRef.current = firmaCarrito(cart);

    (async () => {
      try {
        const r = await validateCart(cart);
        const lineas = Array.isArray(r?.items) ? r.items : [];

        const enCentavos = (v) => Math.round(Number(v || 0) * 100);

        const cambioPrecio = lineas.some((l) => {
          if (l.precio_unitario === null) return false;
          const item = cart.find((c) => c.producto_id === l.producto_id);
          if (!item) return false;
          return (
            enCentavos(item.precio_unitario) !== enCentavos(l.precio_unitario) ||
            enCentavos(item.core_charge) !== enCentavos(l.core_charge)
          );
        });

        if (!r?.puedeAvanzar || cambioPrecio) {
          navigate("/store/cart", { replace: true });
        }
      } catch (e) {
        /* Que falle la consulta no saca al cliente del checkout: el boton
           final vuelve a validar contra el backend y ahi si frena. */
        console.error("Revision del carrito al entrar fallo", e);
      }
    })();
  }, [ready, cart, navigate]);

  /* El carrito cambio en otra pestaña mientras el cliente estaba aca.
   *
   * Ahora las pestañas se sincronizan, asi que el checkout se entera. Y
   * eso hay que atenderlo, no ignorarlo: dejarlo pasar significaria que el
   * pedido crece o se achica mientras el cliente llena el formulario, y se
   * entera cuando le llega la factura. Se lo manda al carrito a revisar lo
   * que quedo.
   */
  useEffect(() => {
    if (!ready) return;
    if (firmaAlEntrarRef.current === null) return;

    if (firmaCarrito(cart) !== firmaAlEntrarRef.current) {
      navigate("/store/cart", { replace: true });
    }
  }, [ready, cart, navigate]);

  useEffect(() => {
    if (!isLogged) return;
    if (perfilCargadoRef.current) return;

    perfilCargadoRef.current = true;

    const loadPerfil = async () => {
      try {
        getPerfilCliente()
          .then((p) => {
            setPerfil(p || null);
            setFactura({
              nombre: p?.nombre || "",
              apellido: p?.apellido || "",
              correo: p?.correo || "",
              codigo_telefono: p?.codigo_telefono || "",
              telefono: p?.telefono || "",
              razon_social: p?.razon_social || "",
              vat_tax_id: p?.vat_tax_id || "",
              correo_facturacion: p?.correo_facturacion || "",
            });

            /* Si falta el nombre legal, el bloque se abre solo: sin el no
               se puede emitir la factura, y esconderlo detras de un click
               es la forma de que nadie lo complete. */
            if (p?.tipo_cliente === "comercial" && !p?.razon_social) {
              setEditandoFactura(true);
            }
          })
          .catch((e) => console.error("Perfil cliente load failed", e));

        const d = await getDireccionesCliente();
        setDirecciones(d || []);

        if (d && d.length > 0) {
          const principal =
            d.find((dir) => dir.direccion_principal === true) || d[0];

          setForm((prev) => ({
            ...prev,
            direccion_guardada: String(principal.cliente_direccion_id),
            /* El telefono de la orden sale del de la direccion; si esa no
               tiene, queda el de la cuenta que ya se cargo antes. */
            telefono_codigo: principal.codigo_telefono || prev.telefono_codigo,
            telefono_numero: principal.telefono || prev.telefono_numero,
            direccion_nombre: principal.nombre_direccion || "",
            direccion_destinatario: principal.nombre_destinatario || "",
            direccion_empresa: principal.empresa || "",
            direccion_linea_1: principal.direccion_linea_1 || "",
            direccion_linea_2: principal.direccion_linea_2 || "",
            ciudad: principal.ciudad || "",
            estado: principal.estado_provincia || "",
            codigo_postal: principal.codigo_postal || "",
            pais: principal.pais || "",
            direccion_principal: true,
          }));

          setStep(2);
        }
      } catch (e) {
        console.error("Perfil load failed", e);
      } finally {
        setLoadingPerfil(false);
      }
    };

    loadPerfil();
  }, [isLogged]);

      useEffect(() => {
        if (metodosCargadosRef.current) return;
        metodosCargadosRef.current = true;

        (async () => {
          try {
            setLoadingMetodosEnvio(true);
            const rows = await getMetodosEnvio();
            setMetodosEnvio(Array.isArray(rows) ? rows : []);
          } catch (e) {
            console.error("Metodos envio load failed", e);
            setMetodosEnvio([]);
          } finally {
            setLoadingMetodosEnvio(false);
          }
        })();
      }, []);


  useEffect(() => {
    if (!ready) return;
    if (loading) return;
    const gr = sessionStorage.getItem("grupo_reserva");
    if (gr) return;
    if (cart.length === 0 && location.pathname !== "/store/cart") {
      navigate("/store/cart", { replace: true });
    }
  }, [ready, loading, cart.length, location.pathname, navigate]);

  useEffect(() => {
    if (!ready) return;
    if (!isLogged && location.pathname !== "/store/cart") {
      navigate("/store/cart", { replace: true });
    }
  }, [ready, isLogged, location.pathname, navigate]);

  useEffect(() => {
    if (!isLogged || !cliente) return;
    if (clienteCargadoRef.current) return;

    clienteCargadoRef.current = true;

    setForm((prev) => ({
      ...prev,
      nombre: cliente.nombre || "",
      apellido: cliente.apellido || "",
      correo: cliente.correo || "",
      telefono_codigo: cliente.codigo_telefono || phoneCountries[0].code,
      telefono_numero: cliente.telefono || "",
    }));
  }, [isLogged, cliente]);

  useEffect(() => {
    if (!selectedMetodo) return;

    setForm((prev) => {
      if (!permiteTarjeta && permiteAgente) {
        return { ...prev, pago_modo: "AGENTE" };
      }
      if (permiteTarjeta && !permiteAgente) {
        return { ...prev, pago_modo: "TARJETA" };
      }
      if (permiteTarjeta && permiteAgente && !prev.pago_modo) {
        return { ...prev, pago_modo: "TARJETA" };
      }
      return prev;
    });
  }, [selectedMetodo, permiteTarjeta, permiteAgente]);


    useEffect(() => {
    if (!selectedMetodo) return;

    if (!requiresAccount) {
      setForm((prev) => ({
        ...prev,
        envio_empresa: "",
        envio_cuenta: "",
      }));
    }
  }, [selectedMetodo, requiresAccount]);

  /* Elegir una direccion guardada copia sus datos al formulario; "new"
     lo deja en blanco para escribir una distinta. Los campos siguen
     editables: lo elegido es un punto de partida, no un candado. */
  const elegirDireccion = (id) => {
    if (id === "new") {
      setForm((prev) => ({
        ...prev,
        direccion_guardada: "new",
        direccion_nombre: "",
        direccion_destinatario: "",
        direccion_empresa: "",
        direccion_linea_1: "",
        direccion_linea_2: "",
        ciudad: "",
        estado: "",
        codigo_postal: "",
        pais: "",
      }));
      return;
    }

    const d = direcciones.find(
      (x) => String(x.cliente_direccion_id) === String(id)
    );
    if (!d) return;

    setForm((prev) => ({
      ...prev,
      direccion_guardada: String(id),
      guardar_direccion: false,
      telefono_codigo: d.codigo_telefono || prev.telefono_codigo,
      telefono_numero: d.telefono || prev.telefono_numero,
      direccion_nombre: d.nombre_direccion || "",
      direccion_destinatario: d.nombre_destinatario || "",
      direccion_empresa: d.empresa || "",
      direccion_linea_1: d.direccion_linea_1 || "",
      direccion_linea_2: d.direccion_linea_2 || "",
      ciudad: d.ciudad || "",
      estado: d.estado_provincia || "",
      codigo_postal: d.codigo_postal || "",
      pais: d.pais || "",
    }));
  };

  /* Se guarda en la cuenta solo si lo pidieron y es una direccion nueva:
     tildar la casilla sobre una ya guardada crearia un duplicado. */
  const guardarDireccionSiCorresponde = async () => {
    if (!form.guardar_direccion) return;
    if (form.direccion_guardada && form.direccion_guardada !== "new") return;

    try {
      await createDireccionCliente({
        nombre_direccion: form.direccion_nombre,
        nombre_destinatario: form.direccion_destinatario,
        empresa: form.direccion_empresa || "",
        codigo_telefono: form.telefono_codigo || "",
        telefono: form.telefono_numero || "",
        direccion_linea_1: form.direccion_linea_1,
        direccion_linea_2: form.direccion_linea_2,
        ciudad: form.ciudad,
        estado_provincia: form.estado,
        codigo_postal: form.codigo_postal,
        pais: form.pais,
        direccion_principal: direcciones.length === 0,
      });

      const d = await getDireccionesCliente();
      setDirecciones(d || []);
    } catch (e) {
      /* Que falle guardarla no puede frenar la compra: la orden lleva la
         direccion igual, esto es solo la libreta del cliente. */
      console.error("No se pudo guardar la direccion", e);
    }
  };

  /* El `+` lo pone la pantalla y no el cliente, igual que en la cuenta: el
     backend exige que el codigo empiece con el, y quien escribe "502" a
     secas no tiene por que enterarse de esa regla al apretar guardar. */
  const limpiarCodigoPais = (valor) => {
    const texto = String(valor ?? "");
    if (texto.trim() === "") return "";
    return `+${texto.replace(/\D/g, "").replace(/^0+/, "").slice(0, 6)}`;
  };

  const handleFacturaChange = (e) => {
    const { name, value } = e.target;

    const limpio =
      name === "codigo_telefono"
        ? limpiarCodigoPais(value)
        : name === "telefono"
        ? value.replace(/\D+/g, "")
        : value;

    setFactura((prev) => ({ ...prev, [name]: limpio }));
  };

  /* El endpoint del perfil valida la ficha entera, asi que se manda
     entera. `empresa` no se edita aca y viaja como esta: si fuera vacio el
     backend borraria el nombre comercial que el cliente cargo en su
     cuenta. */
  const guardarFacturacion = async () => {
    setGuardandoFactura(true);
    setErrorFactura(null);

    try {
      const p = perfil || {};

      await updatePerfilCliente({
        nombre: factura.nombre,
        apellido: factura.apellido,
        correo: factura.correo,
        codigo_telefono: factura.codigo_telefono,
        telefono: factura.telefono,
        empresa: p.empresa || "",
        razon_social: factura.razon_social,
        vat_tax_id: factura.vat_tax_id,
        correo_facturacion: factura.correo_facturacion,
      });

      setPerfil((prev) => ({ ...(prev || {}), ...factura }));

      /* El renglon de arriba y la orden leen de `form`, asi que se
         sincroniza: si no, la compra sale a nombre del dato viejo. El
         telefono no, que ese es el de la direccion de entrega y se
         pregunta aparte mas abajo. */
      setForm((prev) => ({
        ...prev,
        nombre: factura.nombre,
        apellido: factura.apellido,
        correo: factura.correo,
      }));

      setEditandoFactura(false);
    } catch (e) {
      setErrorFactura(
        e?.response?.data?.message || "We couldn't save your details."
      );
    } finally {
      setGuardandoFactura(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const getValidationErrors = (currentStep, form, needsTelefono) => {
    const fields = requiredByStep[currentStep] || [];
    const errs = {};

    fields.forEach((field) => {
      if (!form[field]?.toString().trim()) {
        errs[field] = "Required";
      }
    });

    if (needsTelefono && !form.telefono_numero?.trim()) {
      errs.telefono_numero = "Phone number required";
    }

    if (currentStep === 3 && requiresAccount) {
      if (!form.envio_empresa?.trim()) errs.envio_empresa = "Company name required";
      if (!form.envio_cuenta?.trim()) errs.envio_cuenta = "Account number required";
    }
    if (currentStep === 4) {
      if (selectedMetodo && permiteTarjeta && permiteAgente) {
        if (!form.pago_modo) errs.pago_modo = "Select a payment option";
      }
    }

    return errs;
  };

  const handleFinalAction = async () => {
    setError(null);
    setLoading(true);

    const errsStep2 = getValidationErrors(2, form, needsTelefono);
    const errsStep3 = getValidationErrors(3, form, needsTelefono);
    const errsStep4 = getValidationErrors(4, form, needsTelefono);

    if (
      Object.keys(errsStep2).length ||
      Object.keys(errsStep3).length ||
      Object.keys(errsStep4).length
    ) {
      setErrors({ ...errsStep2, ...errsStep3, ...errsStep4 });
      setError("Please complete all required fields before continuing.");
      setStep(2);
      setLoading(false);
      return;
    }

    try {
      const items = cart.map((p) => ({
        producto_id: p.producto_id,
        cantidad: p.cantidad,
        precio_unitario: Number(p.precio_unitario),
        core_charge: Number(p.core_charge || 0),
      }));

      const reserva = await reservarStock(
        items.map((i) => ({ producto_id: i.producto_id, cantidad: i.cantidad }))
      );

      const grupo_reserva = reserva.grupo_reserva;
      const metodoEnvioId = Number(form.metodo_envio);

      await reservaCheckout({
        grupo_reserva,
        totals: {
          subtotal: Number(subtotal),
          core_charge_total: Number(coreChargeTotal),
          grand_total: Number(grandTotal),
        },
        items,
        telefono: {
          codigo: form.telefono_codigo,
          numero: form.telefono_numero,
        },
        direccion: {
          nombre_direccion: form.direccion_nombre,
          nombre_destinatario: form.direccion_destinatario,
          /* El backend los descarta si no corresponden; se mandan siempre
             para no tener dos formas del mismo objeto segun el tipo de
             cuenta. */
          empresa: form.direccion_empresa || null,
          direccion_linea_1: form.direccion_linea_1,
          direccion_linea_2: form.direccion_linea_2,
          ciudad: form.ciudad,
          estado_provincia: form.estado,
          codigo_postal: form.codigo_postal,
          pais: form.pais,
          direccion_principal: !!form.direccion_principal,
        },
        envio: {
          metodo_envio: metodoEnvioId,
          envio_empresa: form.envio_empresa || null,
          envio_cuenta: form.envio_cuenta || null,
          no_po: form.no_po || null,
          nombre_contacto: form.nombre_contacto || null,
        },
        pago: {
          modo: form.pago_modo || (isStoreSelect ? "AGENTE" : "TARJETA"),
        },
      });

      sessionStorage.setItem("grupo_reserva", grupo_reserva);
      sessionStorage.setItem("checkout_subtotal", String(Number(subtotal)));
      sessionStorage.setItem("checkout_core_charge_total", String(Number(coreChargeTotal)));
      sessionStorage.setItem("checkout_grand_total", String(Number(grandTotal)));
      sessionStorage.setItem("checkout_total", String(Number(grandTotal)));

      const modo = form.pago_modo || (isStoreSelect ? "AGENTE" : "TARJETA");

      if (modo === "TARJETA") {
        navigate("payment");
        return;
      }

      const ord = await crearOrdenPedido(grupo_reserva);

      sessionStorage.setItem("grupo_reserva", String(grupo_reserva));
      if (ord?.pedido_id) sessionStorage.setItem("pedido_id", String(ord.pedido_id));

    navigate("/store/checkout/pending", { replace: true,
      state : {
        pedido_id: ord?.pedido_id || null,
        no_po: ord?.no_po || null,
      },
    });
    
    } catch (err) {
      setError(err.response?.data?.message || "Checkout failed");
    } finally {
      setLoading(false);
    }
  };

  const finalCtaText = useMemo(() => {
    const modo = form.pago_modo || (isStoreSelect ? "AGENTE" : "");
    if (modo === "TARJETA") return "Proceed to Payment";
    if (modo === "AGENTE") return "Generate Order";
    return "Continue";
  }, [form.pago_modo, isStoreSelect]);

  return (
    <div className="adc-checkout-container">
      <div className="adc-checkout-steps">
        <div className={`step ${step >= 2 ? "active" : ""}`}>
          <span>1</span>
          <p>Shipping information</p>
        </div>

        <div className={`line ${step >= 3 ? "active" : ""}`} />

        <div className={`step ${step >= 3 ? "active" : ""}`}>
          <span>2</span>
          <p>Shipping method</p>
        </div>

        <div className={`line ${step >= 4 ? "active" : ""}`} />

        <div className={`step ${step >= 4 ? "active" : ""}`}>
          <span>3</span>
          <p>Payment</p>
        </div>
      </div>

      <div className="adc-checkout-grid">
        <div className="adc-checkout-left">
          <div className="adc-checkout-summary">
            <h2>Order summary</h2>

            {cart.map((item) => {
              const unit = Number(item.precio_unitario);
              const core = Number(item.core_charge || 0);
              const qty = Number(item.cantidad || 0);

              const lineSubtotal = unit * qty;
              const lineCore = core * qty;

              return (
                <div
                  key={item.producto_id}
                  className="adc-summary-item"
                  style={{
                    flexDirection: "column",
                    alignItems: "stretch",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <span>
                      {item.nombre} × {qty}
                    </span>
                    <strong>{formatMoney(lineSubtotal)}</strong>
                  </div>

                  {core > 0 && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        marginTop: 6,
                      }}
                    >
                      <span style={{ color: "#b91c1c", fontWeight: 800 }}>
                        Core Charge {formatMoney(core)} × {qty}
                      </span>
                      <strong style={{ color: "#b91c1c" }}>
                        {formatMoney(lineCore)}
                      </strong>
                    </div>
                  )}
                </div>
              );
            })}

            <div className="adc-summary-total" style={{ marginTop: 14 }}>
              <span>Subtotal </span>
              <strong>{formatMoney(subtotal)}</strong>
            </div>

            {Number(coreChargeTotal) > 0 && (
              <div className="adc-summary-total">
                <span style={{ color: "#000000", fontWeight: 800 }}>
                  Core Charge Total{" "}
                </span>
                <strong style={{ color: "#000000" }}>
                  {formatMoney(coreChargeTotal)}
                </strong>
              </div>
            )}

            {/* No hay renglon de envio: el courier lo cobra aparte contra
                la cuenta del cliente, asi que aca no se calcula nada y el
                total no cambia mas. Decia "Calculated next" y prometia un
                calculo que el paso siguiente nunca hacia. */}
            <div className="adc-summary-total">
              <span>Total</span>
              <strong>{formatMoney(grandTotal)}</strong>
            </div>

            {error && <p className="adc-error">{error}</p>}
          </div>
        </div>

        <div className="adc-checkout-form">
          {step === 2 && (
            <>
              {/* La facturacion en un renglon y no en una tarjeta entera:
                  es un dato de referencia. Pero se puede abrir y corregir
                  aca mismo, porque lo que falta para facturar se descubre
                  justo en este momento y mandar al cliente a su cuenta a
                  mitad de una compra es perder la compra. */}
              <div className="adc-ck-facturacion">
                <div>
                  <span className="adc-ck-facturacion-rotulo">
                    Billing information
                  </span>
                  <p>
                    {esEmpresa
                      ? `${datosCliente.razon_social || "Company name missing"} — ${
                          form.nombre
                        } ${form.apellido}`
                      : `${form.nombre} ${form.apellido}`}
                    {" · "}
                    {(esEmpresa && datosCliente.correo_facturacion) ||
                      form.correo}
                  </p>

                  {datosCliente.telefono ? (
                    <p className="adc-ck-facturacion-dato">
                      {datosCliente.codigo_telefono} {datosCliente.telefono}
                    </p>
                  ) : null}

                  {esEmpresa && datosCliente.vat_tax_id ? (
                    <p className="adc-ck-facturacion-dato">
                      VAT/Tax ID: {datosCliente.vat_tax_id}
                    </p>
                  ) : null}
                </div>

                <button
                  type="button"
                  className="adc-ck-enlace"
                  onClick={() => setEditandoFactura((v) => !v)}
                >
                  {editandoFactura ? "Close" : "Edit billing information"}
                </button>
              </div>

              {editandoFactura ? (
                <div className="adc-ck-factura-editor">
                  {/* Los mismos nombres y el mismo orden que en la cuenta:
                      si aca se llamaran distinto, el cliente creeria que
                      son otros datos y cargaria dos versiones de si
                      mismo. */}
                  <span className="adc-ck-grupo-titulo">
                    Contact information
                  </span>

                  <div className="adc-ck-fila">
                    <div className="adc-ck-campo">
                      <label htmlFor="ck-f-nombre">First name *</label>
                      <input
                        id="ck-f-nombre"
                        name="nombre"
                        autoComplete="given-name"
                        placeholder="Mildrin"
                        maxLength={100}
                        value={factura.nombre}
                        onChange={handleFacturaChange}
                      />
                    </div>

                    <div className="adc-ck-campo">
                      <label htmlFor="ck-f-apellido">Last name *</label>
                      <input
                        id="ck-f-apellido"
                        name="apellido"
                        autoComplete="family-name"
                        placeholder="Lil"
                        maxLength={100}
                        value={factura.apellido}
                        onChange={handleFacturaChange}
                      />
                    </div>
                  </div>

                  <div className="adc-ck-campo">
                    <label htmlFor="ck-f-correo">Email address *</label>
                    <input
                      id="ck-f-correo"
                      name="correo"
                      type="email"
                      autoComplete="email"
                      placeholder="name@company.com"
                      maxLength={120}
                      value={factura.correo}
                      onChange={handleFacturaChange}
                    />
                    <span className="adc-ck-ayuda">
                      Where the order confirmation is sent.
                    </span>
                  </div>

                  {/* Es el telefono de la cuenta, no el de la entrega: ese
                      se pregunta abajo y puede ser el de la bodega. */}
                  <div className="adc-ck-campo">
                    <label htmlFor="ck-f-tel">Phone number *</label>
                    <div className="adc-ck-tel">
                      <input
                        name="codigo_telefono"
                        inputMode="tel"
                        placeholder="+1"
                        maxLength={7}
                        aria-label="Country code"
                        value={factura.codigo_telefono}
                        onChange={handleFacturaChange}
                      />
                      <input
                        id="ck-f-tel"
                        name="telefono"
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        placeholder="7854 2132"
                        maxLength={50}
                        value={factura.telefono}
                        onChange={handleFacturaChange}
                      />
                    </div>
                  </div>

                  {esEmpresa ? (
                    <>
                      <span className="adc-ck-grupo-titulo adc-ck-grupo-titulo--sep">
                        Company details
                      </span>

                      <div className="adc-ck-campo">
                        <label htmlFor="ck-razon">Legal company name *</label>
                        <input
                          id="ck-razon"
                          name="razon_social"
                          autoComplete="organization"
                          placeholder="Company S.A."
                          maxLength={200}
                          value={factura.razon_social}
                          onChange={handleFacturaChange}
                        />
                        <span className="adc-ck-ayuda">
                          Registered name that will appear on invoices.
                        </span>
                      </div>

                      <div className="adc-ck-fila">
                        <div className="adc-ck-campo">
                          <label htmlFor="ck-vat">VAT/Tax ID</label>
                          <input
                            id="ck-vat"
                            name="vat_tax_id"
                            placeholder="1234567-8"
                            maxLength={50}
                            value={factura.vat_tax_id}
                            onChange={handleFacturaChange}
                          />
                          <span className="adc-ck-ayuda">
                            NIT, VAT, EIN or the equivalent in your country.
                          </span>
                        </div>

                        <div className="adc-ck-campo">
                          <label htmlFor="ck-correo-fact">Billing email</label>
                          <input
                            id="ck-correo-fact"
                            name="correo_facturacion"
                            type="email"
                            placeholder={factura.correo}
                            maxLength={120}
                            value={factura.correo_facturacion}
                            onChange={handleFacturaChange}
                          />
                          <span className="adc-ck-ayuda">
                            Only if invoices go somewhere else.
                          </span>
                        </div>
                      </div>
                    </>
                  ) : null}

                  {errorFactura ? (
                    <p className="adc-field-error">{errorFactura}</p>
                  ) : null}

                  <div className="adc-ck-factura-acciones">
                    <button
                      type="button"
                      className="adc-btn adc-back-step"
                      onClick={() => {
                        const p = perfil || {};
                        setFactura({
                          nombre: p.nombre || "",
                          apellido: p.apellido || "",
                          correo: p.correo || "",
                          codigo_telefono: p.codigo_telefono || "",
                          telefono: p.telefono || "",
                          razon_social: p.razon_social || "",
                          vat_tax_id: p.vat_tax_id || "",
                          correo_facturacion: p.correo_facturacion || "",
                        });
                        setErrorFactura(null);
                        setEditandoFactura(false);
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="adc-btn adc-next-btn"
                      onClick={guardarFacturacion}
                      disabled={guardandoFactura}
                    >
                      {guardandoFactura ? "Saving…" : "Save billing details"}
                    </button>
                  </div>
                </div>
              ) : null}

              {/* Las direcciones que el cliente ya tiene guardadas: un
                  comprador frecuente no vuelve a escribir todo. */}
              {direcciones.length > 0 && (
                <div className="adc-ck-guardadas">
                  <span className="adc-ck-grupo-titulo">Saved addresses</span>

                  <div className="adc-ck-guardadas-lista">
                    {direcciones.map((d) => (
                      <label
                        className="adc-ck-guardada"
                        key={d.cliente_direccion_id}
                      >
                        <input
                          type="radio"
                          name="direccion_guardada"
                          value={String(d.cliente_direccion_id)}
                          checked={
                            form.direccion_guardada ===
                            String(d.cliente_direccion_id)
                          }
                          onChange={(e) => elegirDireccion(e.target.value)}
                        />
                        <span className="adc-ck-guardada-caja">
                          <span className="adc-ck-guardada-nombre">
                            {d.nombre_direccion || d.nombre_destinatario}
                          </span>
                          {d.empresa ? (
                            <span className="adc-ck-guardada-linea">
                              {d.empresa}
                            </span>
                          ) : null}
                          <span className="adc-ck-guardada-linea">
                            {[d.direccion_linea_1, d.ciudad, d.pais]
                              .filter(Boolean)
                              .join(", ")}
                          </span>
                          <span className="adc-ck-guardada-linea">
                            {d.nombre_destinatario}
                          </span>
                        </span>
                      </label>
                    ))}

                    <label className="adc-ck-guardada">
                      <input
                        type="radio"
                        name="direccion_guardada"
                        value="new"
                        checked={form.direccion_guardada === "new"}
                        onChange={(e) => elegirDireccion(e.target.value)}
                      />
                      <span className="adc-ck-guardada-caja adc-ck-guardada-nueva">
                        + Add a new address
                      </span>
                    </label>
                  </div>
                </div>
              )}

              <h2>Shipping information</h2>
              <p className="adc-ck-instruccion">
                Enter the address where you want to receive your order.
              </p>

              {/* Dos grupos. Con etiqueta y ejemplo en cada campo:
                  precargada desde la cuenta, el placeholder desaparece y
                  quedaban ocho cajas iguales sin forma de saber cual era
                  cual. Los ejemplos existen porque los nombres se prestan
                  a confusion: el apodo del lugar y quien lo recibe se
                  estaban cargando cruzados. */}
              <div className="adc-ck-grupo">
                <span className="adc-ck-grupo-titulo">Recipient</span>

                <div className="adc-ck-campo">
                  <label htmlFor="ck-nombre">Address label *</label>
                  <input
                    id="ck-nombre"
                    name="direccion_nombre"
                    placeholder="Home, Office, Warehouse"
                    maxLength={150}
                    value={form.direccion_nombre}
                    onChange={handleChange}
                  />
                  {errors.direccion_nombre && (
                    <span className="adc-field-error">
                      {errors.direccion_nombre}
                    </span>
                  )}
                </div>

                <div className="adc-ck-campo">
                  <label htmlFor="ck-destinatario">
                    Recipient&apos;s full name *
                  </label>
                  <input
                    id="ck-destinatario"
                    name="direccion_destinatario"
                    autoComplete="name"
                    placeholder="Mildrin Lil"
                    maxLength={150}
                    value={form.direccion_destinatario}
                    onChange={handleChange}
                  />
                  {errors.direccion_destinatario && (
                    <span className="adc-field-error">
                      {errors.direccion_destinatario}
                    </span>
                  )}
                </div>

                {esEmpresa && (
                  <div className="adc-ck-campo">
                    <label htmlFor="ck-empresa">Company name (optional)</label>
                    <input
                      id="ck-empresa"
                      name="direccion_empresa"
                      autoComplete="organization"
                      placeholder="Company S.A."
                      maxLength={150}
                      value={form.direccion_empresa}
                      onChange={handleChange}
                    />
                  </div>
                )}

                {/* El telefono de la orden: a quien llaman si el
                    transportista no encuentra el lugar. */}
                <div className="adc-ck-campo">
                  <label htmlFor="ck-tel">Phone number *</label>
                  <div className="adc-ck-tel">
                    <input
                      name="telefono_codigo"
                      inputMode="tel"
                      placeholder="+1"
                      maxLength={7}
                      aria-label="Country code"
                      value={form.telefono_codigo}
                      onChange={handleChange}
                    />
                    <input
                      id="ck-tel"
                      name="telefono_numero"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel-national"
                      placeholder="7854 2132"
                      maxLength={50}
                      value={form.telefono_numero}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.telefono_numero && (
                    <span className="adc-field-error">
                      {errors.telefono_numero}
                    </span>
                  )}
                </div>
              </div>

              <div className="adc-ck-grupo">
                <span className="adc-ck-grupo-titulo">Address</span>

                {/* El pais va primero: de el dependen como se llaman y que
                    forma tienen el estado y el codigo postal. */}
                <div className="adc-ck-campo">
                  <label htmlFor="ck-pais">Country *</label>
                  <input
                    id="ck-pais"
                    name="pais"
                    autoComplete="country-name"
                    placeholder="Guatemala"
                    maxLength={100}
                    value={form.pais}
                    onChange={handleChange}
                  />
                  {errors.pais && (
                    <span className="adc-field-error">{errors.pais}</span>
                  )}
                </div>

                <div className="adc-ck-campo">
                  <label htmlFor="ck-linea1">Address line 1 *</label>
                  <input
                    id="ck-linea1"
                    name="direccion_linea_1"
                    autoComplete="address-line1"
                    placeholder="123 Main Street"
                    value={form.direccion_linea_1}
                    onChange={handleChange}
                  />
                  {errors.direccion_linea_1 && (
                    <span className="adc-field-error">
                      {errors.direccion_linea_1}
                    </span>
                  )}
                </div>

                <div className="adc-ck-campo">
                  <label htmlFor="ck-linea2">Address line 2 (optional)</label>
                  <input
                    id="ck-linea2"
                    name="direccion_linea_2"
                    autoComplete="address-line2"
                    placeholder="Apartment, suite, unit"
                    value={form.direccion_linea_2}
                    onChange={handleChange}
                  />
                </div>

                <div className="adc-ck-fila">
                  <div className="adc-ck-campo">
                    <label htmlFor="ck-ciudad">City *</label>
                    <input
                      id="ck-ciudad"
                      name="ciudad"
                      autoComplete="address-level2"
                      placeholder="Guatemala"
                      value={form.ciudad}
                      onChange={handleChange}
                    />
                    {errors.ciudad && (
                      <span className="adc-field-error">{errors.ciudad}</span>
                    )}
                  </div>

                  <div className="adc-ck-campo">
                    <label htmlFor="ck-estado">State / Province *</label>
                    <input
                      id="ck-estado"
                      name="estado"
                      autoComplete="address-level1"
                      placeholder="Guatemala"
                      value={form.estado}
                      onChange={handleChange}
                    />
                    {errors.estado && (
                      <span className="adc-field-error">{errors.estado}</span>
                    )}
                  </div>
                </div>

                <div className="adc-ck-campo adc-ck-campo--corto">
                  <label htmlFor="ck-zip">Postal code *</label>
                  <input
                    id="ck-zip"
                    name="codigo_postal"
                    autoComplete="postal-code"
                    placeholder="01010"
                    value={form.codigo_postal}
                    onChange={handleChange}
                  />
                  {errors.codigo_postal && (
                    <span className="adc-field-error">
                      {errors.codigo_postal}
                    </span>
                  )}
                </div>
              </div>

              {/* Solo tiene sentido si la direccion no viene de la libreta:
                  tildarlo sobre una ya guardada crearia un duplicado. */}
              {(!form.direccion_guardada || form.direccion_guardada === "new") && (
                <label className="adc-ck-casilla">
                  <input
                    type="checkbox"
                    name="guardar_direccion"
                    checked={form.guardar_direccion}
                    onChange={handleChange}
                  />

                  {/* Con el renglon de que hace, y no solo el titulo: la
                      casilla estaba sola en una caja ancha y vacia, y nadie
                      tilda algo sin saber que le van a guardar. */}
                  <span className="adc-ck-casilla-texto">
                    <strong>Save this address for future orders</strong>
                    <span>
                      It goes to your address book, so next time you pick it
                      from the list instead of typing it again.
                    </span>
                  </span>
                </label>
              )}

              <div className="adc-step-actions">
                <button
                  className="adc-btn adc-back-step"
                  onClick={() => navigate("/store/cart")}
                >
                  &larr; Return to cart
                </button>

                <button
                  className="adc-btn adc-next-btn"
                  onClick={async () => {
                    const errs = getValidationErrors(2, form, needsTelefono);
                    setErrors(errs);
                    if (Object.keys(errs).length > 0) return;

                    await guardarDireccionSiCorresponde();
                    setStep(3);
                  }}
                >
                  Continue to shipping method &rarr;
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h2>Shipping method</h2>
              <p className="adc-ck-instruccion">
                Choose the carrier that will deliver your order.
              </p>

              {/* Cada courier en su propia fila y con el renglon de como se
                  cobra: eran cuatro nombres sueltos y el cliente no tenia
                  como saber que UPS le iba a pedir un numero de cuenta
                  hasta que lo elegia y le aparecian dos campos. */}
              <div className="adc-ck-grupo">
                <span className="adc-ck-grupo-titulo">Carrier</span>

                {loadingMetodosEnvio ? (
                  <p className="adc-ck-ayuda">Loading shipping methods…</p>
                ) : metodosEnvio.length === 0 ? (
                  <p className="adc-ck-ayuda">No shipping methods available.</p>
                ) : (
                  <div className="adc-ck-envios">
                    {metodosEnvio.map((m) => {
                      const conCuenta = Number(m.requiere_cuenta) === 1;
                      const loArregla =
                        Number(m.permite_tarjeta) === 0 &&
                        Number(m.permite_agente) === 1;

                      return (
                        <label key={m.metodo_envio_id} className="adc-ck-envio">
                          <input
                            type="radio"
                            name="metodo_envio"
                            value={String(m.metodo_envio_id)}
                            checked={
                              String(form.metodo_envio) ===
                              String(m.metodo_envio_id)
                            }
                            onChange={handleChange}
                          />

                          <span className="adc-ck-envio-texto">
                            <strong>{m.nombre}</strong>
                            <span>
                              {conCuenta
                                ? "Billed to your account with the carrier."
                                : loArregla
                                ? "We will contact you to arrange the shipping and the payment."
                                : "Shipping is billed by the carrier."}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {requiresAccount && (
                <div className="adc-ck-grupo">
                  <span className="adc-ck-grupo-titulo">Carrier account</span>

                  <div className="adc-ck-fila">
                    <div className="adc-ck-campo">
                      <label htmlFor="ck-envio-empresa">
                        Shipping company name *
                      </label>
                      <input
                        id="ck-envio-empresa"
                        name="envio_empresa"
                        placeholder={selectedMetodo?.nombre || "UPS"}
                        maxLength={150}
                        value={form.envio_empresa}
                        onChange={handleChange}
                      />
                      {errors.envio_empresa && (
                        <span className="adc-field-error">
                          {errors.envio_empresa}
                        </span>
                      )}
                    </div>

                    <div className="adc-ck-campo">
                      <label htmlFor="ck-envio-cuenta">Account number *</label>
                      <input
                        id="ck-envio-cuenta"
                        name="envio_cuenta"
                        placeholder="123A456"
                        maxLength={100}
                        value={form.envio_cuenta}
                        onChange={handleChange}
                      />
                      {errors.envio_cuenta && (
                        <span className="adc-field-error">
                          {errors.envio_cuenta}
                        </span>
                      )}
                      <span className="adc-ck-ayuda">
                        The carrier bills the shipping to this account.
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Los dos eran cajas con solo un placeholder adentro: al
                  escribir se perdia el rotulo y no quedaba como saber que
                  era cada una. Opcionales, y dicho. */}
              <div className="adc-ck-grupo">
                <span className="adc-ck-grupo-titulo">
                  Order reference (optional)
                </span>

                <div className="adc-ck-fila">
                  <div className="adc-ck-campo">
                    <label htmlFor="ck-po">PO number</label>
                    <input
                      id="ck-po"
                      name="no_po"
                      placeholder="PO-10254"
                      maxLength={100}
                      value={form.no_po}
                      onChange={handleChange}
                    />
                    <span className="adc-ck-ayuda">
                      Your purchase order, if you use one.
                    </span>
                  </div>

                  <div className="adc-ck-campo">
                    <label htmlFor="ck-contacto">Contact name</label>
                    <input
                      id="ck-contacto"
                      name="nombre_contacto"
                      autoComplete="name"
                      placeholder="Mildrin Lil"
                      maxLength={150}
                      value={form.nombre_contacto}
                      onChange={handleChange}
                    />
                    <span className="adc-ck-ayuda">
                      Who we ask for if we need to call about this order.
                    </span>
                  </div>
                </div>
              </div>

              <div className="adc-step-actions">
                <button className="adc-btn adc-back-step" onClick={() => setStep(2)}>
                  Back
                </button>

                <button
                  className="adc-btn adc-next-btn"
                  onClick={() => {
                    const errs = getValidationErrors(3, form, needsTelefono);
                    setErrors(errs);
                    if (Object.keys(errs).length === 0) setStep(4);
                  }}
                  disabled={!form.metodo_envio}
                >
                  Continue
                </button>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <h2>Review & Next Step</h2>

              {!selectedMetodo ? (
                <p>Please select a shipping method first.</p>
              ) : isStoreSelect ? (
                <p className="adc-review">
                  We will contact you to select the best shipping method and payment option.
                </p>
              ) : (
                <>
                  <p className="adc-review">
                    Choose how you want to continue:
                  </p>

                  {/* Las mismas filas que las del courier: eran radios
                      del sistema con el texto pegado, y encima la regla
                      que pone `display: block` a todos los label del
                      formulario los dejaba apilados. */}
                  <div className="adc-ck-envios">
                    {permiteTarjeta && (
                      <label className="adc-ck-envio">
                        <input
                          type="radio"
                          name="pago_modo"
                          value="TARJETA"
                          checked={form.pago_modo === "TARJETA"}
                          onChange={handleChange}
                        />
                        <span className="adc-ck-envio-texto">
                          <strong>Pay with credit card</strong>
                          <span>You pay now and we prepare the order.</span>
                        </span>
                      </label>
                    )}

                    {permiteAgente && (
                      <label className="adc-ck-envio">
                        <input
                          type="radio"
                          name="pago_modo"
                          value="AGENTE"
                          checked={form.pago_modo === "AGENTE"}
                          onChange={handleChange}
                        />
                        <span className="adc-ck-envio-texto">
                          <strong>Other payment options</strong>
                          <span>We contact you to arrange the payment.</span>
                        </span>
                      </label>
                    )}

                    {errors.pago_modo && (
                      <span className="adc-field-error">{errors.pago_modo}</span>
                    )}
                  </div>
                </>
              )}

              <div className="adc-step-actions">
                <button className="adc-btn adc-back-step" onClick={() => setStep(3)}>
                  Back
                </button>

                <button
                  className="adc-btn adc-pay-btn"
                  disabled={loading}
                  onClick={handleFinalAction}
                >
                  {loading ? "Processing..." : finalCtaText}
                </button>
              </div>
            </>
          )}
        </div>

        {loading && (
          <div className="adc-checkout-overlay">
            <div className="adc-spinner"></div>
            <p>Processing your request…</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Checkout;
